import React, { useState, useEffect, useCallback } from 'react';
import { Connection, PublicKey, VersionedTransaction } from '@solana/web3.js';
import axios from 'axios';
import TokenChart from './TokenChart';
import './TokenDetail.css';

const VITE_ENV = typeof import.meta !== 'undefined' ? import.meta.env : undefined;

const API_URL =
  VITE_ENV?.VITE_API_URL ||
  process.env.REACT_APP_API_URL ||
  'https://trading.omniscalping.com';

const JUPITER_API_KEY = VITE_ENV?.VITE_JUPITER_API_KEY || process.env.REACT_APP_JUPITER_API_KEY || '';
const HELIUS_RPC_URL =
  VITE_ENV?.VITE_HELIUS_RPC_URL || process.env.REACT_APP_HELIUS_RPC_URL || 'https://api.mainnet-beta.solana.com';

// Wallet that collects platform fees.
const OMNI_FEE_WALLET = '4YHey4N4FGy8frzq2pw62JWN6EMdde6PCrcP5eUdLChd';

const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';

// Pay/receive tokens (the non-meme leg of a swap). Decimals are fixed/known.
const PAY_TOKENS = {
  USDC: { symbol: 'USDC', mint: USDC_MINT, decimals: 6, stable: true },
  USDT: { symbol: 'USDT', mint: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB', decimals: 6, stable: true },
  PYUSD: { symbol: 'PYUSD', mint: '2b1kV6DkPAnxd5ixfnxCpjxmKwqjjaYmCZfHsFu24GXo', decimals: 6, stable: true },
  SOL: { symbol: 'SOL', mint: 'So11111111111111111111111111111111111111112', decimals: 9, stable: false },
};

const jupHeaders = () => (JUPITER_API_KEY ? { 'x-api-key': JUPITER_API_KEY } : {});

// Default behavior: trade meme coins against USDC only (guarantees the platform fee is
// always collected in USDC). Set REACT_APP_MEME_ALLOW_NON_USDC / VITE_MEME_ALLOW_NON_USDC
// to "true" to re-enable USDT / SOL / PYUSD as counter-assets.
const ALLOW_NON_USDC_COUNTER =
  String(VITE_ENV?.VITE_MEME_ALLOW_NON_USDC ?? process.env.REACT_APP_MEME_ALLOW_NON_USDC ?? '')
    .toLowerCase() === 'true';

const COUNTER_ASSET_OPTIONS = ALLOW_NON_USDC_COUNTER
  ? ['USDC', 'USDT', 'SOL', 'PYUSD']
  : ['USDC'];

const connection = new Connection(HELIUS_RPC_URL);

// SPL associated-token-account derivation (avoids adding @solana/spl-token).
const TOKEN_PROGRAM_ID = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA');
const ASSOCIATED_TOKEN_PROGRAM_ID = new PublicKey('ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL');

function deriveAta(ownerBase58, mintBase58) {
  const [ata] = PublicKey.findProgramAddressSync(
    [new PublicKey(ownerBase58).toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), new PublicKey(mintBase58).toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID
  );
  return ata.toBase58();
}

// Fees are always collected in USDC → this is the fee wallet's USDC token account.
const OMNI_FEE_USDC_ACCOUNT = deriveAta(OMNI_FEE_WALLET, USDC_MINT);

// Whether the USDC fee account is initialized on-chain. Cache only the positive result
// so that initializing the account later starts collecting fees without a page reload.
let _usdcFeeAccountReady = false;
async function isUsdcFeeAccountReady() {
  if (_usdcFeeAccountReady) return true;
  try {
    const info = await connection.getAccountInfo(new PublicKey(OMNI_FEE_USDC_ACCOUNT));
    _usdcFeeAccountReady = !!info;
  } catch (e) {
    console.warn('USDC fee account check failed:', e.message);
  }
  return _usdcFeeAccountReady;
}

console.log('🔑 Helius API Key available?', !!import.meta.env.VITE_HELIUS_API_KEY);
console.log('🔑 First 10 chars:', import.meta.env.VITE_HELIUS_API_KEY?.substring(0, 10));

const TokenDetail = ({ token, onClose, user, commissionConfig, selectedWallet, connectedWallet, duplicateSymbols, chartMetric = 'price', onChartMetricChange }) => {
  const [loading, setLoading] = useState(false);
  const [tokenData, setTokenData] = useState(token);
  const [activeTab, setActiveTab] = useState('buy'); // 'buy' or 'sell'
  const [amount, setAmount] = useState('');
  const [slippage, setSlippage] = useState(1);
  const [inputToken, setInputToken] = useState('USDC');
  const [showQuote, setShowQuote] = useState(false);
  const [quote, setQuote] = useState(null);
  const [timeframe, setTimeframe] = useState('5m');
  const [devBalance, setDevBalance] = useState(null);
  const [loadingDevBalance, setLoadingDevBalance] = useState(false);

  // Safety net: when non-USDC counter-assets are disabled, force the counter-asset back to USDC.
  useEffect(() => {
    if (!ALLOW_NON_USDC_COUNTER && inputToken !== 'USDC') {
      setInputToken('USDC');
    }
  }, [inputToken]);

  useEffect(() => {
    loadTokenDetails();
    fetchDevBalance();
    
    // Auto-refresh every 15 seconds
    const interval = setInterval(() => {
      loadTokenDetails();
      fetchDevBalance();
    }, 15000);
    
    return () => clearInterval(interval);
  }, [token.id]);

  const loadTokenDetails = useCallback(async () => {
    console.log('📊 Loading details for token:', token.symbol, token.id);

    try {
      // Fetch fresh token data from our backend (which calls Jupiter /assets/gems)
      const response = await axios.get(`${API_URL}/api/meme-coins/token/${token.id}`);

      if (response.data.success && response.data.token) {
        const freshToken = response.data.token;
        
        console.log('✅ Fresh data received:', {
          symbol: freshToken.symbol,
          price: freshToken.usdPrice,
          mcap: freshToken.mcap,
          fdv: freshToken.fdv,
          liquidity: freshToken.liquidity
        });

        // Update tokenData with fresh values from Jupiter
        setTokenData(prevData => ({
          ...prevData,
          usdPrice: freshToken.usdPrice,
          mcap: freshToken.mcap,
          fdv: freshToken.fdv,
          liquidity: freshToken.liquidity,
          circSupply: freshToken.circSupply,
          totalSupply: freshToken.totalSupply,
          stats5m: freshToken.stats5m,
          stats1h: freshToken.stats1h,
          stats6h: freshToken.stats6h,
          stats24h: freshToken.stats24h
        }));
      } else {
        console.log('⚠️ Token not found in recent listings, keeping original data');
      }
    } catch (error) {
      console.log('❌ Backend API Error:', error.message);
      console.log('⚠️ Could not fetch live data, keeping original');
    }
  }, [token.id, token.symbol]);

  const fetchDevBalance = async () => {
    // Use token prop as fallback since tokenData might not be set yet
    const currentToken = tokenData?.dev ? tokenData : token;
    
    console.log('🔍 Attempting to fetch dev balance...');
    console.log('Current token:', currentToken.symbol);
    console.log('Has dev?', !!currentToken.dev);
    console.log('Dev address:', currentToken.dev);
    
    if (!currentToken.dev || !currentToken.id) {
      console.log('⚠️ No dev wallet or token ID found');
      return;
    }
    
    setLoadingDevBalance(true);
    try {
      const heliusApiKey = import.meta.env.VITE_HELIUS_API_KEY;
      if (!heliusApiKey) {
        console.error('❌ VITE_HELIUS_API_KEY not configured in .env file');
        setDevBalance(null);
        setLoadingDevBalance(false);
        return;
      }

      console.log('📡 Querying Helius for dev wallet:', currentToken.dev.substring(0, 8) + '...');
      
      const response = await axios.post(
        `https://mainnet.helius-rpc.com/?api-key=${heliusApiKey}`,
        {
          jsonrpc: '2.0',
          id: 'dev-balance',
          method: 'getTokenAccountsByOwner',
          params: [
            currentToken.dev,
            {
              mint: currentToken.id
            },
            {
              encoding: 'jsonParsed'
            }
          ]
        }
      );
      
      console.log('📡 Helius API response:', response.data);
      
      if (response.data.result?.value?.[0]) {
        const tokenAccount = response.data.result.value[0];
        const balance = tokenAccount.account.data.parsed.info.tokenAmount.uiAmount;
        const percentage = (balance / currentToken.totalSupply) * 100;
        
        setDevBalance({
          tokens: balance,
          percentage: percentage
        });
        
        console.log('✅ Dev Balance:', balance.toLocaleString(), 'tokens', `(${percentage.toFixed(2)}%)`);
      } else {
        setDevBalance({ tokens: 0, percentage: 0 });
        console.log('✅ Dev wallet has 0 tokens (good!)');
      }
    } catch (error) {
      console.error('❌ Error fetching dev balance:', error);
      if (error.response) {
        console.error('API Error:', error.response.data);
      }
      setDevBalance(null);
    } finally {
      setLoadingDevBalance(false);
    }
  };

  const formatNumber = (num) => {
    if (!num) return '$0';
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return '0%';
    const isPositive = value >= 0;
    return (
      <span className={isPositive ? 'positive' : 'negative'}>
        {isPositive ? '+' : ''}{value.toFixed(2)}%
      </span>
    );
  };

  // Resolve true on-chain decimals for the meme token (gems data may omit/incorrectly set it).
  const getMemeDecimals = useCallback(async () => {
    if (Number.isInteger(tokenData?.decimals)) return tokenData.decimals;
    try {
      const info = await connection.getParsedAccountInfo(new PublicKey(tokenData.id));
      const dec = info?.value?.data?.parsed?.info?.decimals;
      if (Number.isInteger(dec)) return dec;
    } catch (e) {
      console.warn('Could not resolve mint decimals, defaulting to 6:', e.message);
    }
    return 6;
  }, [tokenData?.decimals, tokenData?.id]);

  // Fetch a Jupiter quote. Platform fee (in USDC) is only requested when USDC is a
  // swap leg AND the USDC fee account is initialized — otherwise the trade is fee-free.
  const fetchJupQuote = async ({ inputMint, outputMint, amount: amountAtomic, slippageBps, withFee }) => {
    const feeBps = withFee ? Math.floor((commissionConfig?.commission_percent || 0.5) * 100) : 0;
    let url =
      `https://api.jup.ag/swap/v1/quote?inputMint=${inputMint}` +
      `&outputMint=${outputMint}&amount=${amountAtomic}&slippageBps=${slippageBps}`;
    if (feeBps > 0) url += `&platformFeeBps=${feeBps}`;
    const { data } = await axios.get(url, { headers: jupHeaders() });
    if (!data || data.error) throw new Error(data?.error || 'No route found');
    return data;
  };

  // Build a signed-ready swap transaction. Fee is taken in USDC via the fee wallet's
  // USDC token account; only attached when the quote actually carries a platform fee.
  const buildSwapTransaction = async (jupQuote, traderWallet, withFee) => {
    const body = {
      quoteResponse: jupQuote,
      userPublicKey: traderWallet,
      wrapAndUnwrapSol: true,
      prioritizationFeeLamports: 'auto',
    };
    if (withFee) body.feeAccount = OMNI_FEE_USDC_ACCOUNT;
    const { data } = await axios.post('https://api.jup.ag/swap/v1/swap', body, { headers: jupHeaders() });
    if (!data?.swapTransaction) throw new Error(data?.error || 'Swap build failed');
    return VersionedTransaction.deserialize(Buffer.from(data.swapTransaction, 'base64'));
  };

  const handleTrade = async (side) => {
    const traderWallet = connectedWallet || selectedWallet;
    if (!traderWallet) {
      alert('Please connect your wallet first');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    const payToken = PAY_TOKENS[inputToken];
    if (!payToken) {
      alert('Unsupported pay/receive token');
      return;
    }
    if (!tokenData?.id) {
      alert('Token mint address unavailable — cannot quote.');
      return;
    }

    setLoading(true);
    try {
      const memeDecimals = await getMemeDecimals();
      const memeToken = {
        symbol: tokenData.symbol || 'TOKEN',
        mint: tokenData.id,
        decimals: memeDecimals,
        stable: false,
      };

      // BUY: pay stable/SOL -> receive meme. SELL: pay meme -> receive stable/SOL.
      const inputTokenData = side === 'BUY' ? payToken : memeToken;
      const outputTokenData = side === 'BUY' ? memeToken : payToken;

      const inputAmountAtomic = Math.floor(parseFloat(amount) * Math.pow(10, inputTokenData.decimals));
      if (!Number.isFinite(inputAmountAtomic) || inputAmountAtomic <= 0) {
        alert('Amount too small for this token.');
        setLoading(false);
        return;
      }

      // Fee is ONLY ever collected in USDC, so USDC must be one of the two legs.
      const usdcIsLeg = inputTokenData.mint === USDC_MINT || outputTokenData.mint === USDC_MINT;
      const feeInUsdc = usdcIsLeg ? await isUsdcFeeAccountReady() : false;

      const slippageBps = Math.floor(slippage * 100);
      const quoteParams = {
        inputMint: inputTokenData.mint,
        outputMint: outputTokenData.mint,
        amount: inputAmountAtomic,
        slippageBps,
      };

      const quoteData = await fetchJupQuote({ ...quoteParams, withFee: feeInUsdc });

      const outputAmountDecimal = parseFloat(quoteData.outAmount) / Math.pow(10, outputTokenData.decimals);
      const priceImpact = quoteData.priceImpactPct != null ? parseFloat(quoteData.priceImpactPct) * 100 : 0;

      setQuote({
        side,
        inputAmount: amount,
        inputToken: inputTokenData.symbol,
        outputAmount: outputAmountDecimal.toFixed(6),
        outputToken: outputTokenData.symbol,
        priceImpact: Number(priceImpact.toFixed(2)),
        inputTokenData,
        outputTokenData,
        feeInUsdc,
        quoteParams,
        _jupQuote: quoteData,
      });
      setShowQuote(true);
    } catch (error) {
      console.error('Quote error:', error);
      alert('Failed to get quote: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const executeSwap = async () => {
    const traderWallet = connectedWallet || selectedWallet;
    if (!quote?._jupQuote || !traderWallet) {
      alert('Please connect your wallet and get a quote first');
      return;
    }
    if (!window.solana?.signAndSendTransaction) {
      alert('Please install Phantom or a compatible Solana wallet');
      return;
    }

    setLoading(true);
    try {
      const { inputTokenData, outputTokenData } = quote;

      // Build the swap. If the USDC fee causes any build error, transparently rebuild
      // WITHOUT the fee so the user's trade still succeeds (fee is best-effort, never blocking).
      let feeApplied = !!quote.feeInUsdc;
      let jupQuote = quote._jupQuote;
      let transaction;
      try {
        transaction = await buildSwapTransaction(jupQuote, traderWallet, feeApplied);
      } catch (feeErr) {
        if (feeApplied) {
          console.warn('Fee swap build failed, retrying without fee:', feeErr.message);
          feeApplied = false;
          jupQuote = await fetchJupQuote({ ...quote.quoteParams, withFee: false });
          transaction = await buildSwapTransaction(jupQuote, traderWallet, false);
        } else {
          throw feeErr;
        }
      }

      const signResult = await window.solana.signAndSendTransaction(transaction);
      const signature = typeof signResult === 'string' ? signResult : signResult?.signature;
      if (!signature) throw new Error('Wallet did not return a transaction signature');

      // Recompute output from the quote that was actually executed (may differ if we re-quoted).
      const executedOutput = parseFloat(jupQuote.outAmount) / Math.pow(10, outputTokenData.decimals);

      // USD value: prefer the stablecoin leg; for SOL legs estimate from the meme token price.
      const stableLeg = inputTokenData.stable ? inputTokenData : outputTokenData.stable ? outputTokenData : null;
      let inputUsdValue;
      if (inputTokenData.stable) {
        inputUsdValue = parseFloat(quote.inputAmount);
      } else if (outputTokenData.stable) {
        inputUsdValue = executedOutput;
      } else {
        const memeAmount = quote.side === 'BUY' ? executedOutput : parseFloat(quote.inputAmount);
        inputUsdValue = memeAmount * (tokenData.usdPrice || 0);
      }
      const outputUsdValue = inputUsdValue;
      const exchangeRate = executedOutput / parseFloat(quote.inputAmount);
      const tokenPriceUsd = tokenData.usdPrice || null;

      const recordResponse = await fetch(`${API_URL}/api/meme-coins/record-swap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user?.id,
          walletAddress: traderWallet,
          swapType: quote.side === 'BUY' ? 'buy' : 'sell',
          inputMint: inputTokenData.mint,
          inputSymbol: inputTokenData.symbol,
          inputAmount: quote.inputAmount,
          inputDecimals: inputTokenData.decimals,
          inputUsdValue,
          outputMint: outputTokenData.mint,
          outputSymbol: outputTokenData.symbol,
          outputAmount: executedOutput.toFixed(6),
          outputDecimals: outputTokenData.decimals,
          outputUsdValue,
          exchangeRate,
          tokenPriceUsd,
          jupiterPlatformFee: jupQuote.platformFee?.amount || 0,
          jupiterSwapFee: 0,
          gasFee: 0.000005,
          slippageBps: Math.floor(slippage * 100),
          txSignature: signature,
          quoteId: jupQuote.routePlan?.[0]?.swapInfo?.ammKey || null,
          routePlan: jupQuote.routePlan,
          methodology: 'meme_coins',
          swapMethod: 'regular',
          feeMintIsStable: !!stableLeg,
          feeCollectedInUsdc: feeApplied,
        }),
      });

      const recordData = await recordResponse.json();
      if (!recordData.success) {
        throw new Error(recordData.error || 'Failed to record swap');
      }

      const confirmation = await connection.confirmTransaction(signature, 'confirmed');

      await fetch(`${API_URL}/api/meme-coins/confirm-swap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          swapId: recordData.swapId,
          status: confirmation.value.err ? 'failed' : 'confirmed',
          errorMessage: confirmation.value.err ? JSON.stringify(confirmation.value.err) : null,
        }),
      });

      if (confirmation.value.err) {
        alert('⚠️ Swap submitted but failed on-chain. Check your wallet.');
      } else {
        alert(
          `✅ ${quote.side} successful!\n` +
            `Paid: ${quote.inputAmount} ${quote.inputToken}\n` +
            `Received ~${executedOutput.toFixed(6)} ${quote.outputToken}\n` +
            (!feeApplied && quote.feeInUsdc ? '(Platform fee skipped — trade completed.)\n' : '') +
            (recordData.commissionCollected != null
              ? `💰 Commission: $${Number(recordData.commissionCollected).toFixed(2)} · Credit balance: $${Number(recordData.newCreditBalance || 0).toFixed(2)}`
              : '')
        );
      }

      setShowQuote(false);
      setAmount('');
      setQuote(null);
    } catch (error) {
      console.error('Swap error:', error);
      alert('Swap failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Safe local metric state if parent didn't pass handlers
  const effectiveMetric = chartMetric || 'price';
  const handleMetricChange = (metric) => {
    if (onChartMetricChange) {
      onChartMetricChange(metric);
    }
  };

  return (
    <div className="token-detail-overlay" onClick={onClose}>
      <div className="token-detail-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="token-detail-header">
          <div className="token-header-left">
            {tokenData.icon && (
              <img src={tokenData.icon} alt={tokenData.symbol} className="token-detail-logo" />
            )}
            <div className="token-header-info">
              <h2>{tokenData.symbol || 'Unknown'}</h2>
              <p>{tokenData.name || 'Unknown Token'}</p>
            </div>
          </div>
          
          <button className="close-modal-btn" onClick={onClose}>✕</button>
        </div>

        {/* Token Address */}
        <div className="token-address">
          <span className="address-label">Token Address:</span>
          <code className="address-code">{tokenData.id || 'N/A'}</code>
          <button 
            className="copy-btn"
            onClick={() => {
              navigator.clipboard.writeText(tokenData.id);
              alert('Address copied!');
            }}
          >
            📋 Copy
          </button>
        </div>

        {/* ===== CHART AND TRADING SIDE BY SIDE ===== */}
        <div className="chart-trading-container">
          {/* Chart Section (Left) */}
          <div className="chart-section">
            {/* Timeframe + Metric Selector */}
            <div className="chart-controls-row">
              <div className="timeframe-selector">
                {['1m', '5m', '10m', '30m', '1h', '6h', '24h'].map(tf => (
                  <button
                    key={tf}
                    className={timeframe === tf ? 'active' : ''}
                    onClick={() => setTimeframe(tf)}
                  >
                    {tf}
                  </button>
                ))}
              </div>

              <div className="metric-selector">
                <button
                  className={effectiveMetric === 'price' ? 'active' : ''}
                  onClick={() => handleMetricChange('price')}
                >
                  Price
                </button>
                <button
                  className={effectiveMetric === 'marketCap' ? 'active' : ''}
                  onClick={() => handleMetricChange('marketCap')}
                >
                  MC
                </button>
              </div>
            </div>
            
            <TokenChart
              token={tokenData}
              height={400}
              timeframe={timeframe}
              metric={effectiveMetric}
            />
          </div>

          {/* Trading Panel (Right) */}
          <div className="trading-panel">
            <h3>Quick Trade</h3>
            
            {/* Buy/Sell Mode Selector */}
            <div className="trade-mode-selector">
              <button
                className={`trade-mode-btn ${activeTab === 'buy' ? 'active-buy' : ''}`}
                onClick={() => {
                  setActiveTab('buy');
                  setAmount('');
                }}
              >
                Buy
              </button>
              <button
                className={`trade-mode-btn ${activeTab === 'sell' ? 'active-sell' : ''}`}
                onClick={() => {
                  setActiveTab('sell');
                  setAmount('');
                }}
              >
                Sell
              </button>
            </div>

            {activeTab === 'buy' ? (
              /* ===== BUY MODE ===== */
              <>
                {/* Token Selector */}
                <div className="token-selector">
                  <label>Pay with:</label>
                  <select 
                    value={inputToken} 
                    onChange={(e) => setInputToken(e.target.value)}
                    className="token-select"
                    disabled={COUNTER_ASSET_OPTIONS.length === 1}
                  >
                    {COUNTER_ASSET_OPTIONS.map((sym) => (
                      <option key={sym} value={sym}>{sym}</option>
                    ))}
                  </select>
                </div>

                {/* Amount Input */}
                <div className="trade-input-group">
                  <label>Amount ({inputToken})</label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.0"
                    className="trade-input"
                    step="0.01"
                  />
                  {amount && parseFloat(amount) > 0 && (
                    <div className="input-hint">
                      You will receive ~{(parseFloat(amount) / (tokenData.usdPrice || 0.0001)).toFixed(2)} {tokenData.symbol}
                    </div>
                  )}
                </div>

                {/* Slippage Control */}
                <div className="slippage-control">
                  <label>Slippage: {slippage}%</label>
                  <div className="slippage-buttons">
                    {[0.5, 1, 2, 5].map(val => (
                      <button
                        key={val}
                        className={slippage === val ? 'active' : ''}
                        onClick={() => setSlippage(val)}
                      >
                        {val}%
                      </button>
                    ))}
                  </div>
                </div>

                {/* Buy Button */}
                <button 
                  className="buy-btn-full"
                  onClick={() => handleTrade('BUY')}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : `Buy ${tokenData.symbol}`}
                </button>
              </>
            ) : (
              /* ===== SELL MODE ===== */
              <>
                {/* Amount to Sell */}
                <div className="trade-input-group">
                  <label>Amount to Sell ({tokenData.symbol})</label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.0"
                    className="trade-input"
                    step="0.01"
                  />
                  {amount && parseFloat(amount) > 0 && (
                    <div className="input-hint">
                      You will receive ~{(parseFloat(amount) * (tokenData.usdPrice || 0)).toFixed(4)} {inputToken}
                    </div>
                  )}
                </div>

                {/* Receive In */}
                <div className="token-selector">
                  <label>Receive in:</label>
                  <select 
                    value={inputToken} 
                    onChange={(e) => setInputToken(e.target.value)}
                    className="token-select"
                    disabled={COUNTER_ASSET_OPTIONS.length === 1}
                  >
                    {COUNTER_ASSET_OPTIONS.map((sym) => (
                      <option key={sym} value={sym}>{sym}</option>
                    ))}
                  </select>
                </div>

                {/* Slippage Control */}
                <div className="slippage-control">
                  <label>Slippage: {slippage}%</label>
                  <div className="slippage-buttons">
                    {[0.5, 1, 2, 5].map(val => (
                      <button
                        key={val}
                        className={slippage === val ? 'active' : ''}
                        onClick={() => setSlippage(val)}
                      >
                        {val}%
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sell Button */}
                <button 
                  className="sell-btn-full"
                  onClick={() => handleTrade('SELL')}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : `Sell ${tokenData.symbol}`}
                </button>
              </>
            )}

            {/* Commission Info */}
            <div className="commission-info">
              💰 Commission: {commissionConfig?.commission_percent || 0.5}% — collected in USDC
              {inputToken !== 'USDC' && (
                <div style={{ fontSize: '0.8em', opacity: 0.8, marginTop: 4 }}>
                  Tip: use USDC to {activeTab === 'buy' ? 'pay' : 'receive'} so the platform fee applies.
                </div>
              )}
            </div>
          </div>
        </div>
        {/* ===== END CHART AND TRADING ===== */}

        {/* Stats Grid */}
        <div className="token-stats-grid">
          <div className="stat-box">
            <span className="stat-label">Price</span>
            <span className="stat-value">${tokenData.usdPrice?.toFixed(8) || '0.00'}</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">Market Cap</span>
            <span className="stat-value">{formatNumber(tokenData.mcap || tokenData.marketCap)}</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">FDV</span>
            <span className="stat-value">{formatNumber(tokenData.fdv)}</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">Liquidity</span>
            <span className="stat-value">{formatNumber(tokenData.liquidity)}</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">24h Volume</span>
            <span className="stat-value">{formatNumber(tokenData.stats24h?.buyVolume + tokenData.stats24h?.sellVolume)}</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">Holders</span>
            <span className="stat-value">{tokenData.holderCount?.toLocaleString() || 'N/A'}</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">Top 10 Holders</span>
            <span className="stat-value">{tokenData.audit?.topHoldersPercentage?.toFixed(2)}%</span>
          </div>
        </div>

        {/* Price Changes */}
        <div className="price-changes-detail">
          <div className="price-change-box">
            <span className="label">5m</span>
            {formatPercent(tokenData.stats5m?.priceChange || 0)}
          </div>
          <div className="price-change-box">
            <span className="label">1h</span>
            {formatPercent(tokenData.stats1h?.priceChange || 0)}
          </div>
          <div className="price-change-box">
            <span className="label">6h</span>
            {formatPercent(tokenData.stats6h?.priceChange || 0)}
          </div>
          <div className="price-change-box">
            <span className="label">24h</span>
            {formatPercent(tokenData.stats24h?.priceChange || 0)}
          </div>
        </div>

        {/* Audit Info */}
        <div className="audit-section">
          <h3>Security Audit</h3>
          <div className="audit-items">
            <div className={`audit-item ${tokenData.audit?.mintAuthorityDisabled ? 'safe' : 'warning'}`}>
              {tokenData.audit?.mintAuthorityDisabled ? '✅' : '⚠️'} Mint Authority: {tokenData.audit?.mintAuthorityDisabled ? 'Disabled' : 'Enabled'}
            </div>
            <div className={`audit-item ${tokenData.audit?.freezeAuthorityDisabled ? 'safe' : 'warning'}`}>
              {tokenData.audit?.freezeAuthorityDisabled ? '✅' : '⚠️'} Freeze Authority: {tokenData.audit?.freezeAuthorityDisabled ? 'Disabled' : 'Enabled'}
            </div>
            {devBalance !== null ? (
              <div className={`audit-item ${devBalance.percentage > 10 ? 'warning' : 'safe'}`}>
                👨‍💻 Dev Balance: {devBalance.tokens.toLocaleString()} ({devBalance.percentage.toFixed(2)}%)
                {devBalance.percentage > 20 && ' ⚠️ High!'}
              </div>
            ) : loadingDevBalance ? (
              <div className="audit-item">
                👨‍💻 Dev Balance: Loading...
              </div>
            ) : null}
            
            {/* Duplicate Symbol Warning */}
            {duplicateSymbols && duplicateSymbols[tokenData.symbol] && duplicateSymbols[tokenData.symbol].length > 1 && (
              <div className="audit-item warning">
                ⚠️ Multiple tokens with symbol "{tokenData.symbol}" detected ({duplicateSymbols[tokenData.symbol].length} total)
                <div style={{ fontSize: '0.85em', marginTop: '5px', opacity: 0.9 }}>
                  This may indicate a copycat or scam token. Verify the contract address before trading.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Social Links */}
        {(tokenData.twitter || tokenData.website || tokenData.telegram) && (
          <div className="social-links">
            {tokenData.twitter && (
              <a href={tokenData.twitter} target="_blank" rel="noopener noreferrer" className="social-link">
                🐦 Twitter
              </a>
            )}
            {tokenData.website && (
              <a href={tokenData.website} target="_blank" rel="noopener noreferrer" className="social-link">
                🌐 Website
              </a>
            )}
            {tokenData.telegram && (
              <a href={tokenData.telegram} target="_blank" rel="noopener noreferrer" className="social-link">
                💬 Telegram
              </a>
            )}
          </div>
        )}

        {/* Quote Modal */}
        {showQuote && quote && (
          <div className="quote-modal-overlay" onClick={() => setShowQuote(false)}>
            <div className="quote-modal" onClick={(e) => e.stopPropagation()}>
              <h4>Confirm {quote.side}</h4>
              <div className="quote-details">
                <div className="quote-row">
                  <span>You pay:</span>
                  <span className="quote-value">{quote.inputAmount} {quote.inputToken}</span>
                </div>
                <div className="quote-row">
                  <span>You receive (est):</span>
                  <span className="quote-value">{quote.outputAmount} {quote.outputToken}</span>
                </div>
                <div className="quote-row">
                  <span>Price Impact:</span>
                  <span className={`quote-value ${quote.priceImpact > 5 ? 'warning' : ''}`}>
                    {quote.priceImpact}%
                  </span>
                </div>
                <div className="quote-row">
                  <span>Slippage:</span>
                  <span className="quote-value">{slippage}%</span>
                </div>
                <div className="quote-row">
                  <span>Commission:</span>
                  <span className="quote-value">
                    {quote.feeInUsdc
                      ? `${commissionConfig?.commission_percent || 0.5}% (USDC)`
                      : 'None (USDC not in pair)'}
                  </span>
                </div>
              </div>
              <div className="quote-buttons">
                <button className="cancel-quote-btn" onClick={() => setShowQuote(false)}>
                  Cancel
                </button>
                <button className="confirm-quote-btn" onClick={executeSwap} disabled={loading}>
                  {loading ? 'Executing...' : 'Confirm Swap'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TokenDetail;
